import { UPGRADE_PROMPT_ROUTE_CONFIG, type PromptType } from '../config/upgradePromptRoutes';

/**
 * Normalizes a pathname by removing query parameters and hash fragments.
 * This ensures consistent matching regardless of URL parameters.
 *
 * @param pathname - The pathname to normalize (e.g., "/sign-up?engagementId=123#checkout")
 * @returns The normalized pathname (e.g., "/sign-up")
 */
function normalizePath(pathname: string): string {
	// Remove query parameters and hash
	return pathname.split('?')[0].split('#')[0];
}

/**
 * Determines if a prompt of the given type should be shown on the current page.
 *
 * @param promptType - The type of prompt ('modal', 'banner', or 'toast')
 * @param currentPath - The current pathname (e.g., "/dashboard" or "/sign-up?engagementId=123")
 * @returns `true` if the prompt should be shown, `false` otherwise
 *
 * @example
 * ```ts
 * shouldShowPromptOnPage('modal', '/dashboard') // true (modal allowed on dashboard)
 * shouldShowPromptOnPage('banner', '/dashboard') // false (banner excluded on dashboard)
 * shouldShowPromptOnPage('banner', '/parents') // true (banner allowed on parents page)
 * shouldShowPromptOnPage('banner', '/sign-up/checkout') // false (banner excluded on checkout)
 * ```
 */
export function shouldShowPromptOnPage(promptType: PromptType, currentPath: string): boolean {
	const config = UPGRADE_PROMPT_ROUTE_CONFIG[promptType];
	const normalizedPath = normalizePath(currentPath);

	// If allowedPages is specified, path must match one of them (prefix match)
	if (config.allowedPages.length > 0) {
		const matches = config.allowedPages.some((page) => normalizedPath.startsWith(page));
		if (!matches) {
			return false;
		}
		// Even if it matches allowedPages, check if it's explicitly excluded
		if (config.excludedPages.length > 0) {
			return !config.excludedPages.some((page) => normalizedPath.startsWith(page));
		}
		return true;
	}

	// If allowedPages is empty, check excludedPages
	if (config.excludedPages.length > 0) {
		return !config.excludedPages.some((page) => normalizedPath.startsWith(page));
	}

	// Default: show everywhere if no restrictions
	return true;
}

/**
 * Helper to convert GraphQL PromptType enum to config key.
 *
 * @param promptType - The GraphQL enum value (e.g., "MODAL", "BANNER", "TOAST")
 * @returns The config key (e.g., "modal", "banner", "toast")
 */
export function normalizePromptType(promptType: string): PromptType {
	const normalized = promptType.toLowerCase();
	if (normalized === 'modal' || normalized === 'banner' || normalized === 'toast') {
		return normalized as PromptType;
	}
	// Default to 'banner' for unknown types (warn only in development)
	if (process.env.NODE_ENV === 'development') {
		console.warn(`[upgradePromptRouteMatcher] Unknown prompt type: ${promptType}, defaulting to 'banner'`);
	}
	return 'banner';
}
